import { Box } from '@hover/blueprint';
import { useMachine } from '@xstate/react';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSpring, useTransition } from 'react-spring';

import { Meta } from 'src/modules/designStudio/components/DesignStudio/Meta';
import { createMachine } from 'src/modules/designStudio/machines/app';
import { NotFound } from 'src/modules/notFound';
import { FirebaseWarningStyles } from 'src/shared/components';
import { ErrorScope } from 'src/shared/constants';
import { isDevelopment } from 'src/shared/helpers/environment';
import { useErrorScope } from 'src/shared/hooks';
import { useReactNativeWebView } from 'src/shared/hooks/useReactNativeWebView';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { ToastPortal } from 'src/shared/providers/Toaster';

import { Menu } from '../../constants';
import { animationConfig } from '../../helpers/animationConfig';
import { ExtendedMacheteViewer } from '../../helpers/machete';
import { HistoryProvider } from '../../providers';
import { DesignMenu } from '../DesignMenu';
import { NavBar } from '../NavBar';
import { Toolbar } from '../Toolbar';

import { Background } from './Background';
import { Building } from './Building';
import { LoadingPage } from './LoadingPage';
import {
  AnimatedContainer,
  DesignStudioContainer,
  MainContainer,
  PreventOverscroll,
} from './styled';

const DesignStudio = () => {
  // Sets a tag on Sentry with the key of scope for easy filtering
  useErrorScope(ErrorScope.DESIGN_STUDIO);

  const { id } = useParams<{ id: string }>();
  const jobId = parseInt(id, 10);

  // set job id in context for tracking
  const { setMetadata } = useTrackingMetadata();

  useEffect(() => {
    setMetadata({ job_id: jobId });
  }, [setMetadata, jobId]);

  const [state, send] = useMachine(createMachine({ jobId }));
  const loading = state.value === 'loading';
  const jobNotFound = state.value === 'notFound';

  const finishLoading = useCallback(() => send('FINISH_LOADING_ANIMATION'), [
    send,
  ]);
  const finishLoadingBuilding = useCallback(
    () => send('FINISH_LOADING_BUILDING'),
    [send],
  );

  const [viewer, setViewer] = useState<ExtendedMacheteViewer | null>(null);
  const [menu, setMenu] = useState<Menu | null>(null);
  const loadingTransition = useTransition(
    loading,
    null,
    animationConfig.designProScreen,
  );

  const [isWebView] = useReactNativeWebView();

  const buildingTransition = useSpring({
    opacity: loading ? 0 : 1,
    transform: loading ? 'scale(1.2)' : 'scale(1)',
  });

  const transitions = useTransition(!!menu, null, {
    from: {
      transform: 'translate3d(0, 100%,0)',
      opacity: 0,
      position: 'absolute',
      bottom: 0,
      height: '100%',
      width: '100%',
    },
    enter: { transform: 'translate3d(0,0%,0)', opacity: 1 },
    leave: { transform: 'translate3d(0,100%,0)', opacity: 1 },
    config: {
      duration: 350,
    },
  });

  // Not sure if this is the best place for this, but this
  // controls the canvas height / resize depending on the menu
  // state
  const canvasHeight = menu ? '60%' : '100%';

  const closeMenu = () => setMenu(null);

  return (
    <HistoryProvider jobId={jobId}>
      {/* If mobile app, restrict user scaling of document */}
      {isWebView && <Meta />}
      {isDevelopment() && <FirebaseWarningStyles />}
      <DesignStudioContainer
        bottom={0}
        height="100%"
        position="fixed"
        top={0}
        width="100%"
      >
        <PreventOverscroll />
        {jobNotFound ? (
          <NotFound />
        ) : (
          <MainContainer style={buildingTransition}>
            <Background />
            <Building
              canvasHeight={canvasHeight}
              jobId={jobId}
              menu={menu}
              setLoadingBuilding={finishLoadingBuilding}
              setViewer={setViewer}
            />
            <NavBar menu={menu} setMenu={setMenu} />

            <ToastPortal />
            <Box flexDirection="column">
              {transitions.map(({ item, props: menuStyle, key }) =>
                item ? (
                  <Fragment key={key}>
                    <AnimatedContainer style={menuStyle}>
                      <DesignMenu
                        closeMenu={closeMenu}
                        jobId={jobId}
                        menu={menu}
                        viewer={viewer}
                      />
                    </AnimatedContainer>
                  </Fragment>
                ) : (
                  <AnimatedContainer key={key} style={menuStyle}>
                    <Toolbar setMenu={setMenu} />
                  </AnimatedContainer>
                ),
              )}
            </Box>
          </MainContainer>
        )}
        {loadingTransition.map(({ item: isLoading, props: styles, key }) =>
          isLoading ? (
            <MainContainer key={key} style={styles}>
              <LoadingPage finishLoading={finishLoading} />
            </MainContainer>
          ) : (
            <Box display="block" key={key} />
          ),
        )}
      </DesignStudioContainer>
    </HistoryProvider>
  );
};

export { DesignStudio };
